import { Component, HostListener, inject, signal, type OnDestroy, type OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { type ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, Subject, take, takeUntil, withLatestFrom } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../authentication/_services/auth.service';
import { ChapterContextService } from '../../../modules/chapter/chapter-context.service';
import { NotificationService } from '../../../modules/notifications/notification.service';
import { type Language, type UserInfo } from '../../../modules/user-management/users/user.model';
import { UserService } from '../../../modules/user-management/users/user.service';
import { PasswordExpiryService } from '../../components/password-expiry-modal/services/password-expiry.service.component';
import { CustomToastrService } from '../../services/custom-toastr.service';
import { LanguageService } from '../../services/language.service';
import { ResponsiveService } from '../../services/responsive.service';
import { SharedService } from '../../services/shared.service';
import { ThemeService } from '../../services/theme.service';
import { TranslationService } from '../../services/translation.service';

@Component({
  selector: 'app-full',
  templateUrl: './full.component.html',
  styleUrl: './full.component.scss',
  standalone: false
})
export class FullComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  authService = inject(AuthService);
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  responsive = inject(ResponsiveService);
  private languageService = inject(LanguageService);
  private customToastr = inject(CustomToastrService);
  private translationService = inject(TranslationService);
  private notificationService = inject(NotificationService);
  private sharedService = inject(SharedService);
  passwordExpiryService = inject(PasswordExpiryService);
  themeService = inject(ThemeService);
  private chapterContext = inject(ChapterContextService);

  /**
   * "RMBF <chapter name>" once the logged-in chapter's summary loads (September 2026) — was the
   * hardcoded "RMBF BBC Chapter", wrong for every other chapter. Chapter-neutral until then.
   */
  get applicationTitle(): string {
    const name = this.chapterContext.summary()?.name?.trim();
    return name ? `RMBF ${name}` : 'RMBF';
  }

  /**
   * The chapter's own logo when it has uploaded one on Chapter Settings, else the common RMBF
   * logo (September 2026). The common one also covers the moment before the summary loads.
   */
  get toolbarLogoUrl(): string {
    return this.sharedService.resolveFileImageSrc(this.chapterContext.summary()?.logo, environment.tempImageUrl) || this.erpLogoUrl;
  }

  private readonly _userInfo = toSignal(this.authService.userInfo$, { initialValue: null });
  private readonly _unreadCount = this.notificationService.unreadCount;

  get userInfo(): UserInfo | null {
    return this._userInfo();
  }
  get unreadNotificationCount(): number {
    return this._unreadCount();
  }
  get notificationBadgeLabel(): string {
    const c = this._unreadCount();
    return c > 99 ? '99+' : `${c}`;
  }

  languages = signal<Language[]>([]);
  languageControl = new FormControl('');
  isSidenavOpen = false;
  screenWidth!: number;
  erpLogoUrl = environment.erpLogoUrl;
  codatronLogoUrl = environment.codatronLogoUrl;
  isApplicationSupport = false;

  readonly darkModeEnabled = ThemeService.DARK_MODE_ENABLED;

  get isDarkMode(): boolean {
    return this.themeService.isDark();
  }

  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.authService.isLoggedIn$
      .pipe(
        filter(isLoggedIn => isLoggedIn === true),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.initializeLanguageControl();
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.responsive.isHandset()) {
          this.isSidenavOpen = false;
        }
      });
  }

  get userName(): string {
    return this.userInfo ? [this.userInfo.fName, this.userInfo.lName].filter(Boolean).join(' ') : '';
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.screenWidth = event.target.innerWidth;
  }

  async ngOnInit(): Promise<void> {
    this.isApplicationSupport = this.authService.getUserRoles().includes('ApplicationSupport');
    // A page refresh already has the chapter id; a fresh login gets it from updateLocalProfile below.
    this.chapterContext.load();
    this.languageService.getLanguagesForDropdown().subscribe({
      next: (res: Language[]) => {
        this.languages.set(res);
      }
    });

    this.authService.isLoggedIn$
      .pipe(
        takeUntil(this.destroy$),
        filter(isLoggedIn => isLoggedIn === true),
        withLatestFrom(this.authService.userInfo$),
        filter(([isLoggedIn, info]) => {
          console.log('isLoggedIn', isLoggedIn);
          return info === null;
        }),
        take(1)
      )
      .subscribe(() => {
        this.userService.getLoggedInUserProfile().subscribe({
          next: profile => {
            this.authService.updateCurrentUserInfo(profile);
            this.authService.updateLocalProfile(profile);
            this.chapterContext.load();
            // TDD 13 §7.5 - apply the member's saved language preference on login, rather than leaving
            // whatever locale ngx-translate defaulted to (browser language / last device's localStorage
            // value, which may not be this member's own preference on a shared/new device).
            const profileLangCode = profile.language?.code;
            if (profileLangCode && profileLangCode !== this.translate.currentLang) {
              this.translationService.setLanguage(profileLangCode).then(() => {
                this.initializeLanguageControl();
              });
            }
          }
        });
      });

    this.initializeComponent();
    this.initializeLanguageControl();
    this.setupLanguageControlListener();
    this.setupNotificationListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidenav(): void {
    this.isSidenavOpen = !this.isSidenavOpen;
  }

  toggleDarkMode(): void {
    this.themeService.toggle();
  }

  onSidenavOpenedChange(opened: boolean): void {
    if (this.responsive.isHandset()) {
      this.isSidenavOpen = opened;
    }
  }

  onLogout(): void {
    this.authService.logout();
  }

  private initializeComponent(): void {
    this.screenWidth = window.innerWidth;
  }

  private initializeLanguageControl(): void {
    const currentLang = this.translate.currentLang;
    this.languageControl.setValue(currentLang, { emitEvent: false });
  }

  private setupLanguageControlListener(): void {
    this.languageControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(lang => {
      if (lang && lang !== this.translate.currentLang) {
        this.translationService.setLanguage(lang);
      }
    });
  }

  getDeepestRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  getCurrentLanguageLabel(): string {
    const currentLang = this.translate.currentLang;
    const language = this.languages().find(lang => lang.code === currentLang);
    return language ? language.name : 'UNKNOWN';
  }

  setupNotificationListener(): void {
    this.notificationService.connect();

    if (!this.passwordExpiryService.hasPasswordExpired()) {
      this.notificationService.getUnreadCount().subscribe();
    }
  }

  redirectToHomePage(): void {
    this.customToastr.success(this.translate.instant('COMMON.REDIRECTING_TO_HOME_PAGE'));
    setTimeout(() => {
      window.open(environment.orgUrl, '_blank');
    }, 2000);
  }

  /*
   * viewProfile() was removed in September 2026 along with the Profile menu item.
   *
   * It had never worked: it navigated to /users/profile/view, angular-base's
   * user-management route, which this app never registered - RMBF has `members`,
   * not `users` - so it fell through to the `**` wildcard and returned to the
   * dashboard.
   *
   * It was dropped rather than repointed because this console is officer-only
   * (WebConsoleAccessGuard: officers, plus the ApplicationSupport login), so nobody needs a
   * self-service profile page - an officer reaches and edits their own record
   * through Members like any other. angular-base's profileMode exists to let a
   * user without master-data rights edit their own record and to hide admin
   * actions from them; neither applies to anyone who can reach this menu.
   *
   * Note for whoever revisits this: `languageCode` is member self-service only
   * (MemberFormComponent#isSelfEdit), and the toolbar language switcher above is
   * session-scoped - it calls translationService.setLanguage() and does not
   * persist Member.language, which is what decides the language an officer's
   * push and email notifications arrive in (TDD 13 §5). Changing that from this
   * console now means Members -> your own record -> Edit.
   */

  changePassword(): void {
    this.router.navigate(['/change-password']);
  }

  redirectToCodatron(): void {
    this.customToastr.success(this.translate.instant('COMMON.CODATRON_HOMEPAGE'));
    setTimeout(() => {
      window.open(environment.codatronUrl, '_blank');
    }, 2000);
  }
}
